import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box, Typography, TextField, IconButton, Button, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Checkbox, useTheme,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import SendIcon from "@mui/icons-material/Send";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import { listDataTables } from "../utils/api";
import { useImpersonation } from "../contexts/ImpersonationContext";

/**
 * Unified entry surface for the analyze agent — used from the Explore page
 * and from a project's Report tab (empty state). One composer; three intents:
 *
 *   1. attach a CSV    → source="file"
 *   2. attach tables   → source="tables"
 *   3. just a question → source="goal" (agent walks user through what data)
 *
 * Props:
 *   datasetName?  — when set, the table-picker shows tables from this dataset
 *                   (the caller's org dataset by default; can be left to the
 *                   server to resolve via /data/tables)
 *   submitting    — bool, disables the composer while a submission is in flight
 *   error?        — string, optional banner above the composer
 *   onSubmitFile     (file, message?)        — handler for path 1
 *   onSubmitTables   (tablesArray, message?) — handler for path 2
 *   onSubmitGoal     (message)               — handler for path 3
 */
export default function AnalyzeEntryComposer({
  submitting,
  error,
  onSubmitFile,
  onSubmitTables,
  onSubmitGoal,
}) {
  const theme = useTheme();
  // Starting an analysis is a write — block it when viewing a user read-only.
  const { viewAsReadOnly, actingUser } = useImpersonation();
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [tables, setTables] = useState([]); // [{dataset, table}] — auto-filled on mount
  const [autoAttachedCount, setAutoAttachedCount] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Auto-attach: on first mount, pull all the user's loaded tables and
  // make them available to the agent by default. The user can still
  // constrain via the picker, or remove individual chips, but the common
  // case (user has data, types a question) needs zero extra clicks.
  useEffect(() => {
    listDataTables()
      .then((data) => {
        const dataset = data?.dataset;
        const rows = data?.tables || [];
        if (!dataset || rows.length === 0) return;
        const refs = rows
          .map((t) => (typeof t === "string" ? t : t?.table || t?.name))
          .filter(Boolean)
          .map((table) => ({ dataset, table }));
        setTables(refs);
        setAutoAttachedCount(refs.length);
      })
      .catch(() => { /* no loaded data — Path 1 / Path 3 still work */ });
  }, []);

  const onDrop = useCallback((accepted) => {
    if (accepted?.length > 0) {
      setFile(accepted[0]);
      setTables([]);          // file + tables are mutually exclusive
      setAutoAttachedCount(0);
    }
  }, []);
  const { getRootProps, getInputProps, isDragActive, open: openFileDialog } =
    useDropzone({
      onDrop,
      accept: { "text/csv": [".csv"] },
      multiple: false,
      disabled: submitting,
      noClick: true,         // we trigger via the paperclip button, not the whole box
      noKeyboard: true,
    });

  const removeFile = () => setFile(null);
  const removeTable = (idx) => {
    setTables((prev) => prev.filter((_, i) => i !== idx));
    // After any manual edit, the count is no longer "auto" — switch the
    // indicator wording from "in scope" to "selected."
    setAutoAttachedCount(0);
  };

  // Tables alone never enable Send. Auto-attached tables are *context*, not a
  // query — without a typed question we'd inject a fake "Please profile X..."
  // first message and the user would see what looks like an unfamiliar chat
  // already in progress. Require either an explicit file upload (the file is
  // the input) or typed text.
  const canSend = !submitting && !viewAsReadOnly && (file || message.trim());

  const handleSend = () => {
    if (!canSend) return;
    const trimmed = message.trim();
    if (file) {
      onSubmitFile(file, trimmed || undefined);
    } else if (tables.length > 0) {
      onSubmitTables(tables, trimmed);
    } else {
      onSubmitGoal(trimmed);
    }
  };

  return (
    <Box>
      {error && (
        <Box
          sx={{
            mb: 1.5, px: 1.5, py: 1, borderRadius: 1,
            bgcolor: `${theme.palette.error.main}15`,
          }}
        >
          <Typography variant="caption" color="error.main">{error}</Typography>
        </Box>
      )}

      <Box
        {...getRootProps()}
        sx={{
          border: `1.5px solid ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: 2.5,
          p: 1.5,
          bgcolor: isDragActive ? `${theme.palette.primary.main}08` : "background.paper",
          transition: "all 0.15s",
        }}
      >
        <input {...getInputProps()} />

        {/* Attached items. For up to 3 tables we render individual chips so
            the user sees what's in scope at a glance; beyond that we collapse
            to a single summary chip that opens the picker (cluttered chips
            stop being informative). The file (Path 1) is always a single
            chip when present. */}
        {(file || tables.length > 0) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
            {file && (
              <Chip
                icon={<InsertDriveFileIcon sx={{ fontSize: 16 }} />}
                label={file.name}
                size="small"
                onDelete={submitting ? undefined : removeFile}
                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: 12 }}
              />
            )}
            {!file && tables.length > 0 && tables.length <= 3 && tables.map((t, i) => (
              <Chip
                key={`${t.dataset}.${t.table}`}
                icon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
                label={t.table}
                size="small"
                onDelete={submitting ? undefined : () => removeTable(i)}
                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: 12 }}
              />
            ))}
            {!file && tables.length > 3 && (
              <Chip
                icon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
                label={`${tables.length} tables in scope${autoAttachedCount === tables.length ? "" : " (custom)"}`}
                size="small"
                onClick={() => setPickerOpen(true)}
                sx={{ fontSize: 12, cursor: "pointer" }}
              />
            )}
          </Box>
        )}

        {/* Multiline input */}
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={10}
          placeholder={
            viewAsReadOnly
              ? `Read-only — viewing ${actingUser?.email || "this user"}. Starting an analysis is disabled.`
              : isDragActive
                ? "Drop the CSV file…"
                : file || tables.length > 0
                  ? "Describe what you'd like to analyze (optional)…"
                  : "What would you like to analyze? Describe your goal, or drop a CSV / pick tables."
          }
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSend) {
              e.preventDefault();
              handleSend();
            }
          }}
          variant="standard"
          disabled={submitting || viewAsReadOnly}
          InputProps={{ disableUnderline: true, sx: { fontSize: 14 } }}
        />

        {/* Actions row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
          <Button
            size="small"
            startIcon={<AttachFileIcon sx={{ fontSize: 16 }} />}
            onClick={openFileDialog}
            disabled={submitting || viewAsReadOnly}
            sx={{ textTransform: "none", color: "text.secondary", fontSize: 12 }}
          >
            Upload CSV
          </Button>
          <Button
            size="small"
            startIcon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setPickerOpen(true)}
            disabled={submitting || viewAsReadOnly}
            sx={{ textTransform: "none", color: "text.secondary", fontSize: 12 }}
          >
            {tables.length === 0
              ? "Pick tables"
              : autoAttachedCount === tables.length
                ? "Change tables"
                : "Edit selection"}
          </Button>
          <Box sx={{ flex: 1 }} />
          {submitting ? (
            <CircularProgress size={20} />
          ) : (
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!canSend}
              size="small"
              sx={{
                bgcolor: canSend ? theme.palette.primary.main : undefined,
                color: canSend ? "white" : undefined,
                "&:hover": { bgcolor: canSend ? theme.palette.primary.dark : undefined },
                "&.Mui-disabled": { color: "text.disabled" },
              }}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Helper text — adapts to current state. */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1, ml: 0.5 }}
      >
        {file
          ? "Ready to analyze your uploaded file. Add a question or just send."
          : tables.length > 0
            ? (autoAttachedCount === tables.length
                ? `Using all ${tables.length} of your loaded table${tables.length === 1 ? "" : "s"}. Just ask your question.`
                : `Using ${tables.length} selected table${tables.length === 1 ? "" : "s"}.`)
            : "Drop a file, or just describe what you want to analyze and we'll suggest the data you need."}
      </Typography>

      <TablePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selected={tables}
        onConfirm={(picked) => {
          setTables(picked);
          setFile(null);
          setPickerOpen(false);
        }}
      />
    </Box>
  );
}

// ─── Table picker dialog ─────────────────────────────────────────────────────

function TablePickerDialog({ open, onClose, selected, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tables, setTables] = useState([]);
  const [dataset, setDataset] = useState(null);
  const [picked, setPicked] = useState([]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError("");
    setPicked(selected || []);
    listDataTables()
      .then((data) => {
        setTables(data.tables || []);
        setDataset(data.dataset || null);
      })
      .catch((e) => setError(e?.response?.data?.error || e.message || "Failed to load"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (table) => {
    setPicked((prev) => {
      const idx = prev.findIndex((p) => p.dataset === dataset && p.table === table);
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      return [...prev, { dataset, table }];
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Pick tables</DialogTitle>
      <DialogContent sx={{ minHeight: 200 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error.main">{error}</Typography>
        ) : tables.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No tables loaded yet. Load data on the Data page first.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {tables.map((t) => {
              const name = typeof t === "string" ? t : (t.table || t.name);
              const isPicked = picked.some((p) => p.dataset === dataset && p.table === name);
              return (
                <ListItem key={name} disablePadding>
                  <ListItemButton onClick={() => toggle(name)}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Checkbox
                        edge="start"
                        checked={isPicked}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={name}
                      primaryTypographyProps={{ variant: "body2" }}
                      secondary={typeof t === "object" && t.row_count
                        ? `${t.row_count.toLocaleString()} rows`
                        : null}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(picked)}
          sx={{ textTransform: "none", boxShadow: "none" }}
        >
          {picked.length === 0
            ? "Use no tables"
            : `Use ${picked.length} table${picked.length === 1 ? "" : "s"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
